import { startScanner } from "../scan";
import { escapeHTML, REASON_COPY } from "../ui";
import type { Scanner } from "./scan";
import { ICON } from "../icons";

/**
 * One scan for the errand and challenge screens: a camera under `prompt`,
 * stopped at the first payload, which goes to `onPayload`. Returns the
 * function that stops whichever camera is running, for the screen's teardown.
 *
 * `refuse`, when given, reads each payload first and returns a sentence for one
 * this screen cannot use; the sentence goes under the camera and the camera
 * keeps running, so a person pointing at the wrong code only has to move the
 * phone.
 *
 * The same guards as `renderScan`, for the same reasons: `stop` is reached
 * through a `let` because a payload can arrive before `scanner` resolves, and
 * a camera that will not open says so instead of leaving a black rectangle.
 */
export async function scanOnce(
  root: HTMLElement,
  prompt: string,
  onPayload: (bytes: Uint8Array) => void,
  scanner: Scanner = startScanner,
  refuse?: (bytes: Uint8Array) => string | null,
): Promise<() => void> {
  let stop: (() => void) | null = null;
  let stopped = false;

  const deliver = (payload: Uint8Array) => {
    if (stopped) return;
    const refusal = refuse?.(payload);
    if (refusal) {
      const hint = root.querySelector<HTMLParagraphElement>("#hint");
      if (hint) hint.textContent = refusal;
      return;
    }
    stopped = true;
    stop?.();
    onPayload(payload);
  };

  const open = async () => {
    root.innerHTML = `<div class="viewfinder"><video id="camera" playsinline muted></video></div>
                      <p>${escapeHTML(prompt)}</p>
                      <p id="hint" class="warn" aria-live="polite"></p>`;
    const video = root.querySelector<HTMLVideoElement>("#camera")!;
    try {
      stop = await scanner(video, deliver);
    } catch {
      root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div>
        <h1>Sin cámara</h1><p>${escapeHTML(REASON_COPY["camera-unavailable"])}</p>
        <div class="foot"><button id="retry">Reintentar</button></div></div>`;
      root.querySelector<HTMLButtonElement>("#retry")!
        .addEventListener("click", () => void open());
      return;
    }
    if (stopped) stop();
  };

  await open();
  return () => {
    stopped = true;
    stop?.();
  };
}
